import { addDays, combineDateAndTime, toDateKey } from "./date";
import type { Appointment, Id } from "../types";

export interface AvailableSlot {
  startsAt: string;
  endsAt: string;
  dateKey: string;
  isWeekend: boolean;
}

const WORK_DAY_START_HOUR = 8;
const WORK_DAY_END_HOUR = 18;
const SLOT_STEP_MINUTES = 30;
const SUGGESTION_DAYS = 7;
const MAX_SLOTS_PER_DAY = 3;

/*
 * TODO(full MVP): move working hours and the 30-minute slot granularity to
 * user settings. Add separate options for showing weekend and public-holiday
 * suggestions. When disabled, weekend/holiday tiles should not be proposed,
 * while manual date and time entry must remain possible. Mark weekends and
 * holidays in the availability legend when they are displayed.
 */

function overlaps(start: Date, end: Date, appointment: Appointment) {
  return start.getTime() < new Date(appointment.endsAt).getTime()
    && end.getTime() > new Date(appointment.startsAt).getTime();
}

export function getAppointmentConflicts(
  appointments: Appointment[],
  startsAt: Date,
  endsAt: Date,
  ignoredAppointmentId?: Id,
) {
  return appointments.filter((appointment) => appointment.id !== ignoredAppointmentId && overlaps(startsAt, endsAt, appointment));
}

export function getAvailableSlots(
  appointments: Appointment[],
  suggestionStartDate: string,
  durationMinutes: number,
  now: Date,
  ignoredAppointmentId?: Id,
) {
  const slots: AvailableSlot[] = [];

  for (let dayOffset = 0; dayOffset < SUGGESTION_DAYS; dayOffset += 1) {
    const dateKey = addDays(suggestionStartDate, dayOffset);
    const date = new Date(`${dateKey}T12:00:00`);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const daySlots: AvailableSlot[] = [];

    for (let minutes = WORK_DAY_START_HOUR * 60; minutes < WORK_DAY_END_HOUR * 60; minutes += SLOT_STEP_MINUTES) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const start = combineDateAndTime(dateKey, `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
      const end = new Date(start.getTime() + durationMinutes * 60_000);

      if (end.getHours() * 60 + end.getMinutes() > WORK_DAY_END_HOUR * 60) continue;
      if (toDateKey(start) === toDateKey(now) && start.getTime() < now.getTime()) continue;
      if (getAppointmentConflicts(appointments, start, end, ignoredAppointmentId).length > 0) continue;

      daySlots.push({ startsAt: start.toISOString(), endsAt: end.toISOString(), dateKey, isWeekend });
      if (daySlots.length === MAX_SLOTS_PER_DAY) break;
    }

    slots.push(...daySlots);
  }

  return slots;
}
